import React from "react";
import {
  Button,
  FormFieldSet,
  useFormHandler,
} from "../../../../../../components";

// Strips dash placeholders so edit fields start empty instead of showing "-"
const nd = (val) => {
  if (val === null || val === undefined || val === "-") return "";
  if (typeof val === "object" && val !== null) {
    if (val.value === "-" || val.label === "-") return "";
    return val.value || val.label || "";
  }
  return val;
};

// Converts DD-MM-YYYY → MM-DD-YYYY for the datepicker
const toPickerDate = (val) => {
  if (!val || val === "-") return "";
  if (typeof val === "string" && /^\d{2}-\d{2}-\d{4}$/.test(val)) {
    const [dd, mm, yyyy] = val.split("-");
    return `${mm}-${dd}-${yyyy}`;
  }
  return val;
};

const EditEmployeeDetails = ({ data, onClose, onSave }) => {
  const initialFormData = {
    firstName: nd(data?.employee?.firstName),
    middleName: nd(data?.employee?.middleName),
    lastName: nd(data?.employee?.lastName),
    email: nd(data?.employee?.email),
    alternateEmail: nd(data?.employee?.alternateEmail),
    phoneNumber: {
      code: nd(data?.employee?.phone?.countryCode),
      number: nd(data?.employee?.phone?.number),
    },
    gender: data?.employee?.gender ? String(data.employee.gender) : undefined,
    dob: toPickerDate(nd(data?.employee?.dob)),
    classification: data?.employee?.classification ? String(data.employee.classification) : "1",
    employmentType: data?.employee?.employmentType ? String(data.employee.employmentType) : "1",
    jobTitle: nd(data?.employee?.placement?.jobTitle),
    startDate: toPickerDate(nd(data?.employee?.startDate || data?.employee?.placement?.startDate)),
    addressLine1: nd(data?.employee?.address?.addressLine1),
    addressLine2: nd(data?.employee?.address?.addressLine2),
    city: nd(data?.employee?.address?.city),
    state: nd(data?.employee?.address?.state),
    country: nd(data?.employee?.address?.country) || "United States",
    zipCode: nd(data?.employee?.address?.zipCode),
  };

  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(initialFormData, {
      firstName: { type: "text", required: true },
      middleName: { type: "text" },
      lastName: { type: "text", required: true },
      email: { type: "email", required: true },
      alternateEmail: { type: "email" },
      phoneNumber: { type: "phone", required: true },
      jobTitle: { type: "text", required: true },
      gender: { type: "select" },
      dob: { type: "datepicker" },
      classification: { type: "select" },
      employmentType: { type: "select" },
      startDate: { type: "datepicker", required: true },
      addressLine1: { type: "text", required: true },
      country: { type: "countries", required: true },
      city: { type: "cities", required: true },
      state: { type: "states", required: true },
      zipCode: { type: "text", required: true, number: true },
    });

  const onFieldChange = (field, value) => {
    const label = value?.label || value;
    const valueForValidation = typeof label === "number" ? String(label) : label;
    
    handleChange(field, valueForValidation);

    // Auto-update addressLine1 if city, state, country or zipCode changes
    if (["city", "state", "country", "zipCode"].includes(field)) {
      setFormData((prev) => {
        const currentData = { ...prev, [field]: valueForValidation };

        // Dependency resets: if country changes, clear state and city. If state changes, clear city.
        if (field === "country") {
          currentData.state = "";
          currentData.city = "";
        } else if (field === "state") {
          currentData.city = "";
        }
        
        const newAddr = [
          currentData.city,
          currentData.state,
          currentData.zipCode,
          currentData.country,
        ]
          .filter(Boolean)
          .join(", ");
        return {
          ...currentData,
          addressLine1: newAddr || "",
        };
      });
    }
  };

  const handleSave = () => {
    const isValid = validateAllFields();
    if (!isValid) return;
    if (onSave) onSave(formData);
  };

  return (
    <div className="flex flex-col gap-2 p-5">
      <div className="grid grid-cols-4 gap-5">
        <FormFieldSet
          title="First Name"
          type="text"
          placeholder="First Name"
          value={formData.firstName}
          onChange={(value) => handleChange("firstName", value)}
          error={errors.firstName}
        />
        <FormFieldSet
          title="Middle Name"
          type="text"
          placeholder="Middle Name"
          value={formData.middleName}
          onChange={(value) => handleChange("middleName", value)}
          error={errors.middleName}
        />
        <FormFieldSet
          title="Last Name"
          type="text"
          placeholder="Last Name"
          value={formData.lastName}
          onChange={(value) => handleChange("lastName", value)}
          error={errors.lastName}
        />
        <FormFieldSet
          title="Email Id"
          type="text"
          placeholder="Email Id"
          value={formData.email}
          onChange={(value) => handleChange("email", value)}
          error={errors.email}
        />
        <FormFieldSet
          title="Alternate Email"
          type="text"
          placeholder="Alternate Email"
          value={formData.alternateEmail}
          onChange={(value) => handleChange("alternateEmail", value)}
          error={errors.alternateEmail}
        />
        <FormFieldSet
          title="Phone"
          type="phone"
          placeholder="Phone"
          code={formData.phoneNumber.code}
          value={formData.phoneNumber.number}
          onChange={(fullValue, details) =>
            handleChange("phoneNumber", {
              code: details.countryCode,
              number: details.number,
            })
          }
          error={errors.phoneNumber}
        />
        <FormFieldSet
          title="Gender"
          type="select"
          options={[
            { label: "Male", value: 1 },
            { label: "Female", value: 2 },
            { label: "Wish not to specify", value: 3 },
            { label: "Other", value: 4 },
          ]}
          value={formData.gender}
          onChange={(value) => onFieldChange("gender", value)}
          error={errors.gender}
        />
        <FormFieldSet
          title="DOB"
          type="datepicker"
          value={formData.dob}
          onChange={(value) => handleChange("dob", value)}
          error={errors.dob}
        />
        <FormFieldSet
          title="Classification"
          type="select"
          options={[
            { label: "Billable", value: 1 },
            { label: "Non-Billable", value: 2 },
          ]}
          value={formData.classification}
          onChange={(value) => onFieldChange("classification", value)}
          error={errors.classification}
        />
        <FormFieldSet
          title="Employment Type"
          type="select"
          options={[
            { label: "Regular", value: 1 },
            { label: "Permanent", value: 2 },
            { label: "Contractor", value: 3 },
            { label: "Reference", value: 4 },
          ]}
          value={formData.employmentType}
          onChange={(value) => onFieldChange("employmentType", value)}
          error={errors.employmentType}
        />
        <FormFieldSet
          title="Job Title"
          type="text"
          placeholder="Job Title"
          value={formData.jobTitle}
          onChange={(value) => handleChange("jobTitle", value)}
          error={errors.jobTitle}
        />
        <FormFieldSet
          title="Start Date"
          type="datepicker"
          placeholder="Start Date"
          value={formData.startDate}
          onChange={(value) => handleChange("startDate", value)}
          error={errors.startDate}
        />
        <FormFieldSet
          title="Address Line 1"
          type="text"
          placeholder="Address Line 1"
          value={formData.addressLine1}
          onChange={(value) => handleChange("addressLine1", value)}
          error={errors.addressLine1}
        />
        <FormFieldSet
          title="Address Line 2"
          type="text"
          placeholder="Address Line 2"
          value={formData.addressLine2}
          onChange={(value) => handleChange("addressLine2", value)}
          error={errors.addressLine2}
        />
        <FormFieldSet
          title="Country"
          type="countries"
          placeholder="Country"
          value={formData.country || ""}
          onChange={(value) => onFieldChange("country", value)}
          error={errors.country}
        />
        <FormFieldSet
          title="State"
          type="states"
          parentValue={{ country: formData.country || "" }}
          value={formData.state || ""}
          placeholder="State"
          onChange={(value) => onFieldChange("state", value)}
          error={errors.state}
        />
        <FormFieldSet
          title="City"
          type="cities"
          parentValue={{
            country: formData.country || "",
            state: formData.state || ""
          }}
          placeholder="City"
          value={formData.city || ""}
          onChange={(value) => onFieldChange("city", value)}
          error={errors.city}
        />
        <FormFieldSet
          title="Zip Code"
          type="text"
          placeholder="Zip Code"
          value={formData.zipCode}
          onChange={(value) => onFieldChange("zipCode", value)}
          error={errors.zipCode}
        />
      </div>
      <div className="flex gap-3 justify-end">
        <Button title="Cancel" variant="outlined" onClick={onClose} />
        <Button title="Save" onClick={() => handleSave()} />
      </div>
    </div>
  );
};

export default EditEmployeeDetails;
